import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { UserPlus, X } from 'lucide-react';
import * as Yup from 'yup';
import { PartnerEntity } from '../../../../../utils/types/admin_types';
import { FormattedSelectOptions } from '../../../../../utils/types/analytics/analytics_types';

export type AddPartnerEmployeeModalProps = {
  open: boolean;
  onClose: () => void;
  partnerEntityList: PartnerEntity[];
  addPartnerEmployee: (
    email: string,
    firstName: string,
    lastName: string,
    partnerEntityId: FormattedSelectOptions,
    jobTitle: string,
  ) => void;
};

export const getFormattedEntityList = (
  partnerEntities: PartnerEntity[],
): FormattedSelectOptions[] => {
  const formattedOrgList: FormattedSelectOptions[] = [];
  const processPartnerEntity = (
    level: number,
    partnerEntity?: PartnerEntity,
  ) => {
    if (!partnerEntity) return;
    formattedOrgList.push({
      value: partnerEntity.id,
      label: partnerEntity.name,
      level,
    });
    for (const org of partnerEntity.subEntities) {
      processPartnerEntity(level + 1, org);
    }
  };
  for (const partnerEntity of partnerEntities) {
    processPartnerEntity(0, partnerEntity);
  }
  return formattedOrgList;
};

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email address is required'),
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  partnerEntity: Yup.object()
    .nullable()
    .required('Partner entity organization is required'),
  jobTitle: Yup.string().trim().required('Job title is required'),
});

export const AddPartnerEmployeeModal: React.FC<AddPartnerEmployeeModalProps> = ({
  open,
  onClose,
  partnerEntityList,
  addPartnerEmployee,
}) => {
  const formattedOrgList = useMemo(
    () => getFormattedEntityList(partnerEntityList),
    [partnerEntityList],
  );

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      partnerEntity: null as FormattedSelectOptions | null,
      jobTitle: '',
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      if (values.partnerEntity) {
        addPartnerEmployee(
          values.email.trim(),
          values.firstName.trim(),
          values.lastName.trim(),
          values.partnerEntity,
          values.jobTitle.trim(),
        );
        resetForm();
        onClose();
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle
          sx={{
            p: 2.5,
            pb: 2,
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UserPlus size={20} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  lineHeight: 1.2,
                }}
              >
                Invite Partner Employee
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Add a new user and assign them to a partner organization
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: '#94a3b8',
              '&:hover': { bgcolor: '#f8fafc', color: '#0f172a' },
            }}
          >
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 2.5,
            pt: '20px !important',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.2,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                name="firstName"
                label="First Name"
                placeholder="e.g. John"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                name="lastName"
                label="Last Name"
                placeholder="e.g. Doe"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            size="small"
            name="email"
            type="email"
            label="Email Address"
            placeholder="e.g. user@partner.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <Autocomplete
            options={formattedOrgList}
            getOptionLabel={(option) => {
              if (option.level && option.level > 0) {
                return `${'— '.repeat(option.level)}${option.label}`;
              }
              return option.label;
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            value={formik.values.partnerEntity}
            onChange={(_, newValue) => {
              formik.setFieldValue('partnerEntity', newValue);
            }}
            onBlur={() => formik.setFieldTouched('partnerEntity', true)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Select Partner Entity"
                placeholder="Choose partner organization..."
                error={
                  formik.touched.partnerEntity &&
                  Boolean(formik.errors.partnerEntity)
                }
                helperText={
                  formik.touched.partnerEntity &&
                  (formik.errors.partnerEntity as string)
                }
              />
            )}
          />

          <TextField
            fullWidth
            size="small"
            name="jobTitle"
            label="Job Title"
            placeholder="e.g. Operations Specialist"
            value={formik.values.jobTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
            helperText={formik.touched.jobTitle && formik.errors.jobTitle}
          />
        </DialogContent>

        <DialogActions
          sx={{
            p: 2.5,
            pt: 1.5,
            borderTop: '1px solid #f1f5f9',
            gap: 1,
          }}
        >
          <MuiButton
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2,
              color: '#475569',
              borderColor: '#cbd5e1',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<UserPlus size={16} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 2.5,
              bgcolor: '#2563eb',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                bgcolor: '#1d4ed8',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
              },
            }}
          >
            Invite Employee
          </MuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
